/**
 * Client Supplemental Mixin
 *
 * Wraps the V3 ClientSupplemental service so components don't have to
 * re-implement the same get / create-or-update pattern that's used for
 * client-level configuration values.
 *
 * Usage:
 *   import clientSupplementalMixin from "@/mixins/clientSupplementalMixin";
 *
 *   export default {
 *     mixins: [clientSupplementalMixin],
 *     methods: {
 *       async load() {
 *         const { value, clientSupplementalId } = await this.getClientSupplemental({
 *           clientId: this.globalClientId,
 *           key: "claim_audit_percentage",
 *           errorContext: "Failed to get audit configuration",
 *         });
 *         this.auditPercent = Number(value ?? 0);
 *         this.clientSupplementalId = clientSupplementalId;
 *       },
 *       async save() {
 *         const { clientSupplementalId } = await this.saveClientSupplemental({
 *           clientId: this.globalClientId,
 *           key: "claim_audit_percentage",
 *           value: String(this.auditPercent),
 *           clientSupplementalId: this.clientSupplementalId,
 *           updateErrorContext: "Failed to update audit configuration",
 *           createErrorContext: "Failed to create audit configuration",
 *         });
 *         this.clientSupplementalId = clientSupplementalId;
 *       },
 *     },
 *   };
 *
 * Methods provided:
 * - getClientSupplemental({ clientId, key, errorContext })
 *     Reads the latest row for `key` and returns
 *     `{ value, clientSupplementalId }` (both null if no row exists).
 * - saveClientSupplemental({ clientId, key, value, clientSupplementalId, ... })
 *     If `clientSupplementalId` is provided, patches that row; otherwise
 *     creates a new one. Returns `{ clientSupplementalId, created }`.
 */

import ClientServiceV2 from "@/services/ClientServiceV2";

export default {
  data() {
    return {
      clientSupplementalService: new ClientServiceV2(),
    };
  },
  methods: {
    /**
     * Fetch a single client-supplemental row by key.
     *
     * @param {Object} options
     * @param {number|string} options.clientId
     * @param {string} options.key
     * @param {string} [options.errorContext]
     * @returns {Promise<{ value: any, clientSupplementalId: number|string|null }>}
     *   Returns `{ value: null, clientSupplementalId: null }` when no row exists.
     */
    async getClientSupplemental({ clientId, key, errorContext }) {
      if (!clientId) throw new Error("getClientSupplemental: clientId is required");
      if (!key) throw new Error("getClientSupplemental: key is required");

      const response = await this.clientSupplementalService.listClientSupplementalV3(
        { clientId, limit: 1, key },
        errorContext
      );
      const rows = response?.result?.rows || [];
      if (rows.length === 0) {
        return { value: null, clientSupplementalId: null };
      }
      return {
        value: rows[0].value ?? null,
        clientSupplementalId: rows[0].clientSupplementalId ?? null,
      };
    },

    /**
     * Create a new client-supplemental row, or update an existing one when
     * `clientSupplementalId` is provided.
     *
     * @param {Object} options
     * @param {number|string} options.clientId
     * @param {string} options.key
     * @param {*} options.value - Coerced to a string before sending.
     * @param {number|string|null} [options.clientSupplementalId]
     *   When truthy, the row is patched; otherwise a new row is created.
     * @param {string} [options.updateErrorContext]
     * @param {string} [options.createErrorContext]
     * @returns {Promise<{ clientSupplementalId: number|string|null, created: boolean }>}
     */
    async saveClientSupplemental({
      clientId,
      key,
      value,
      clientSupplementalId = null,
      updateErrorContext,
      createErrorContext,
    }) {
      if (!clientId) throw new Error("saveClientSupplemental: clientId is required");
      if (!key) throw new Error("saveClientSupplemental: key is required");

      const stringValue = value === null || value === undefined ? "" : String(value);

      if (clientSupplementalId) {
        await this.clientSupplementalService.updateClientSupplementalV3(
          { key, clientId },
          { value: stringValue },
          updateErrorContext
        );
        return { clientSupplementalId, created: false };
      }

      const response = await this.clientSupplementalService.createClientSupplementalV3(
        { clientId, key, value: stringValue },
        createErrorContext
      );
      return {
        clientSupplementalId: response?.result?.clientSupplementalId ?? null,
        created: true,
      };
    },

    /**
     * Soft-delete a client-supplemental row by patching its status to "Deleted".
     *
     * @param {Object} options
     * @param {number|string} options.clientId
     * @param {string} options.key
     * @param {string} [options.errorContext]
     */
    async softDeleteClientSupplemental({ clientId, key, errorContext }) {
      if (!clientId) throw new Error("softDeleteClientSupplemental: clientId is required");
      if (!key) throw new Error("softDeleteClientSupplemental: key is required");

      await this.clientSupplementalService.updateClientSupplementalV3(
        { key, clientId },
        { status: "Deleted" },
        errorContext
      );
    },
  },
};
